import { useState, useEffect } from 'react';
import { calculateDistance } from '../utils/distanceCalculator';

interface AddressStepProps {
  data: any;
  updateData: (data: any) => void;
  nextStep: () => void;
  prevStep: () => void;
}

const GARAGE_ADDRESS = 'Z.I. Champ Cheval 2, 1530 Payerne, Suisse';

export default function AddressStep({
  data,
  updateData,
  nextStep,
  prevStep,
}: AddressStepProps) {
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');

  // Trigger distance calculation after the user has filled the address fields
  useEffect(() => {
    if (
      data.location === 'domicile' &&
      data.address &&
      data.postalCode &&
      data.city
    ) {
      const timer = setTimeout(() => {
        handleCalculateDistance();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [data.address, data.postalCode, data.city, data.location]);

  const handleCalculateDistance = async () => {
    if (!data.address || !data.postalCode || !data.city) return;

    setCalculating(true);
    setError('');

    try {
      const result = await calculateDistance(
        GARAGE_ADDRESS,
        `${data.address}, ${data.postalCode} ${data.city}, Suisse`
      );

      if (result.success) {
        const distanceInKm = Math.round(result.distance / 1000);
        const travelCost = Math.round(distanceInKm * 1.5 * 100) / 100;

        updateData({
          distance: distanceInKm,
          travelCost,
        });
      } else {
        setError(result.error || 'Impossible de calculer la distance');
        updateData({ distance: 0, travelCost: 0 });
      }
    } catch (err) {
      setError('Erreur lors du calcul de la distance');
      updateData({ distance: 0, travelCost: 0 });
    } finally {
      setCalculating(false);
    }
  };

  const canContinue = () => {
    if (data.location === 'garage') return true;
    return (
      data.address &&
      data.postalCode &&
      data.city &&
      data.distance > 0 &&
      !calculating
    );
  };

  // ---------- GARAGE VIEW ----------
  if (data.location === 'garage') {
    return (
      <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
        <button
          onClick={prevStep}
          className="flex items-center text-primary hover:text-primary/80 transition-colors duration-300 mb-4"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Retour
        </button>

        <h2 className="text-2xl font-bold text-white mb-6">Lieu de service</h2>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 text-center mb-6">
          <i className="ri-map-pin-line text-4xl text-primary mb-3"></i>
          <p className="text-white font-semibold mb-2">Au Garage</p>
          <p className="text-gray-400 text-sm">{GARAGE_ADDRESS}</p>
        </div>

        <button
          onClick={nextStep}
          className="w-full py-4 bg-primary text-dark-bg rounded-xl font-bold text-lg hover:bg-primary/90 transition-all duration-300 whitespace-nowrap"
        >
          Continuer →
        </button>
      </div>
    );
  }

  // ---------- Domicile view ----------
  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
      <button
        onClick={prevStep}
        className="flex items-center text-primary hover:text-primary/80 transition-colors duration-300 mb-4"
      >
        <i className="ri-arrow-left-line mr-2"></i>
        Retour
      </button>

      <h2 className="text-2xl font-bold text-white mb-6">
        Adresse de prise en charge
      </h2>

      <div className="space-y-4 mb-6">
        {/* Street address */}
        <div>
          <label className="block text-white font-semibold mb-2">
            <i className="ri-road-map-line mr-2"></i>
            Rue et numéro *
          </label>
          <input
            type="text"
            value={data.address}
            onChange={(e) => updateData({ address: e.target.value })}
            className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
            placeholder="Ex: Route de Lausanne 10"
          />
        </div>

        {/* Postal code & city */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-white font-semibold mb-2">
              <i className="ri-mail-line mr-2"></i>
              Code postal *
            </label>
            <input
              type="text"
              value={data.postalCode}
              onChange={(e) => updateData({ postalCode: e.target.value })}
              className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
              placeholder="Ex: 1530"
            />
          </div>
          <div>
            <label className="block text-white font-semibold mb-2">
              <i className="ri-map-pin-line mr-2"></i>
              Ville *
            </label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => updateData({ city: e.target.value })}
              className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
              placeholder="Ex: Payerne"
            />
          </div>
        </div>

        {/* Loading indicator */}
        {calculating && (
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 text-primary text-sm">
            <div className="flex items-center">
              <i className="ri-loader-4-line animate-spin mr-2 text-xl"></i>
              <span>Calcul de la distance en cours...</span>
            </div>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <i className="ri-error-warning-line text-red-400 text-xl mt-0.5"></i>
              <div>
                <p className="text-red-400 font-semibold mb-1">
                  Adresse introuvable
                </p>
                <p className="text-red-300 text-sm">{error}</p>
                <button
                  onClick={handleCalculateDistance}
                  className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1"></i>
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success display */}
        {data.distance > 0 && !calculating && !error && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <i className="ri-map-pin-distance-line text-green-400 text-xl"></i>
                <span className="text-white font-semibold">
                  Distance calculée
                </span>
              </div>
              <span className="text-green-400 font-bold text-xl">
                {data.distance} km
              </span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-green-500/30">
              <span className="text-gray-300 text-sm">
                Frais de déplacement (1.50 CHF/km)
              </span>
              <span className="text-green-400 font-bold text-lg">
                {data.travelCost} CHF
              </span>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={nextStep}
        disabled={!canContinue()}
        className="w-full py-4 bg-primary text-dark-bg rounded-xl font-bold text-lg hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      >
        Continuer →
      </button>
    </div>
  );
}
